import { useLoader, Vector3 } from '@react-three/fiber';
import { useEffect, useState } from 'react';
import { TextureLoader } from 'three';
import { degToRad } from 'three/src/math/MathUtils';
import { EmoteDictionary, AnswerState } from './enums/ThreeEnums';
import { EmoteProps, EmoteStackProps, HeartsProps, ImageProps } from './interfaces/ThreeInterfaces';

export function SpriteButton(props: ImageProps) {
    const [tempScale, setTempScale] = useState(1);
    const [dimensions, setDimensions] = useState([0, 0]);
    const texture = useLoader(TextureLoader, props.texture);

    useEffect(() => {
        const img = new Image();
        img.src = props.texture;
        img.onload = () => {
            const ratio = img.width / img.height;
            if (props.width) {
                setDimensions([props.width, props.width / ratio]);
            } else if (props.height) {
                setDimensions([props.height * ratio, props.height]);
            }
        }
    }, [props])

    return (
        <mesh {...props} onPointerEnter={() => { setTempScale(1.03); setHovered(true) }} onPointerLeave={() => { setTempScale(1); setHovered(false) }} onPointerDown={() => { setTempScale(1) }} onPointerUp={() => { setTempScale(1.03) }}>
            <planeGeometry args={[dimensions[0] * tempScale, dimensions[1] * tempScale]}></planeGeometry>
            <meshBasicMaterial map={texture} transparent={true}></meshBasicMaterial>
        </mesh >
    )
}

export function SpriteImage(props: ImageProps) {
    const [dimensions, setDimensions] = useState([0, 0]);
    const texture = useLoader(TextureLoader, props.texture);

    useEffect(() => {
        const img = new Image();
        img.src = props.texture;
        img.onload = () => {
            const ratio = img.width / img.height;
            if (props.width) {
                setDimensions([props.width, props.width / ratio]);
            } else if (props.height) {
                setDimensions([props.height * ratio, props.height]);
            }
        }
    }, [props.width, props.height, texture, props.texture])

    return (
        <mesh {...props}>
            <planeGeometry args={[dimensions[0], dimensions[1]]}></planeGeometry>
            <meshBasicMaterial map={texture} transparent={true}></meshBasicMaterial>
        </mesh >
    )
}

export function Emote(props: EmoteProps) {
    const [answer, setAnswer] = useState(<></>);

    useEffect(() => {
        switch (props.state) {
            case AnswerState.Correct: {
                setAnswer(<SpriteImage position={[0, props.width ? props.width / 1.3 : 0, 100]} width={props.width ? props.width / 2 : 0} height={props.width ? props.width / 2 : 0} texture={'emotes/Correct.png'}></SpriteImage>)
                break;
            }
            case AnswerState.Wrong: {
                setAnswer(<SpriteImage position={[0, props.width ? props.width / 1.3 : 0, 100]} width={props.width ? props.width / 2 : 0} height={props.width ? props.width / 2 : 0} texture={'emotes/Wrong.png'}></SpriteImage>)
                break;
            }
            case AnswerState.None: {
                setAnswer(<></>);
                break;
            }
        }
    }, [props])

    return (
        <mesh {...props}>
            <mesh rotation={[0, degToRad(props.flipped ? props.flipped % 360 : 0), 0]}>
                <SpriteButton width={props.width} height={props.height} texture={`emotes/${EmoteDictionary[props.index % (Object.keys(EmoteDictionary).length / 2)]}.png`}></SpriteButton>
                <SpriteButton width={props.width} height={props.height} rotation={[0, degToRad(180), 0]} texture={'emotes/Blank.png'}></SpriteButton>
            </mesh>
            {answer}
        </mesh>
    )
}

export function EmoteStack(props: EmoteStackProps) {
    const [emoteArray, setEmoteArray] = useState<any>();

    useEffect(() => {
        const totalX = Math.min(props.maxPerLine, props.emotes.length);
        const totalY = Math.floor((props.emotes.length - 1) / props.maxPerLine) + 1;

        const offsetPosition = {
            x: (totalX - 1) * -(props.width / 2),
            y: (totalY - 1) * (props.lineHeight / 2)
        };

        const temp: any[] = [];
        props.emotes.forEach((emote, index) => {
            const currentX = index % props.maxPerLine;
            const currentY = Math.floor(index / props.maxPerLine);
            temp.push({
                index: emote,
                position: [props.width * currentX + offsetPosition.x, -props.lineHeight * currentY + offsetPosition.y, 0],
                onClickFunction: () => {
                    if (props.onEmoteClick) {
                        props.onEmoteClick(emote);
                    }
                },
                state: props.wrongAnswers?.filter(numb => numb === index).length ? AnswerState.Wrong : props.correctAnswers?.filter(numb => numb === index).length ? AnswerState.Correct : AnswerState.None
            })
        });
        setEmoteArray(temp);
    }, [props]);

    return (
        <group {...props}>
            {
                emoteArray?.map((emote: { position: Vector3; index: number, onClickFunction: { (args: any): any }, state: AnswerState }, index: number) => {
                    return <Emote index={emote.index} key={index} width={props.width} position={emote.position} onClick={emote.onClickFunction} flipped={props.flipped} state={emote.state}></Emote>
                })
            }
        </group >
    )
}

export function setHovered(hovered: boolean) {
    document.body.style.cursor = hovered ? 'pointer' : 'auto'
}

export function Hearts(props: HeartsProps) {
    const [tempArray, setTempArray] = useState([0]);

    useEffect(() => {
        setTempArray([...Array(props.amount)]);
    }, [props])

    return (
        <group>
            {
                tempArray.map((o, i) => (
                    <Emote key={i} position={[-60 * i, 0, 0]} width={50} index={EmoteDictionary.Live} state={AnswerState.None}></Emote>
                ))
            }
        </group>

    )
}